/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/* Import i18n functions */
import { useI18n } from '@/context/I18nContext';

/* Import configuration */
import {
    MUENSTER_DEVICE_SENSOR_CONFIG,
    NEUTRAUBLING_DEVICE_SENSOR_CONFIG,
} from '@/config/deviceSensorConfig';
import { KPI_OVERVIEW_GROUPS } from '@/config/kpi/tools/overviewConfig';

/* Import libraries */
import { DeviceSensor } from '@/lib/sensors/catalog/deviceSensor';
import { DeviceSensorCatalog } from '@/lib/sensors/catalog/deviceSensorCatalog';

const KPI_DEVICE_SENSOR_CATALOGS = {
    muenster: new DeviceSensorCatalog(MUENSTER_DEVICE_SENSOR_CONFIG),
    neutraubling: new DeviceSensorCatalog(NEUTRAUBLING_DEVICE_SENSOR_CONFIG),
};

function KpisTab() {
    const { t } = useI18n();

    return (
        <div className="flex flex-col gap-8">
            <section className="flex flex-col gap-2">
                <h2>{t('KPIs')}</h2>
            </section>

            {KPI_OVERVIEW_GROUPS.map((groupConfig) => {
                const deviceSensor = new DeviceSensor(
                    groupConfig.exampleDevice,
                    KPI_DEVICE_SENSOR_CATALOGS[groupConfig.customer],
                );
                const sensorTypes = deviceSensor.getSensorTypes(groupConfig.group);

                return (
                    <section
                        key={groupConfig.group}
                        className="flex flex-col gap-3"
                    >
                        <h3>{t(groupConfig.title)}</h3>
                        <p>{t(groupConfig.overview)}</p>
                        <div className="flex items-center gap-2 text-sm text-base-content/80">
                            <span className="font-semibold">{t('Group')}:</span>
                            <code className="rounded bg-base-200 px-1.5 py-0.5 font-mono text-sm">
                                {groupConfig.group}
                            </code>
                        </div>

                        <div className="overflow-x-auto rounded-box border border-base-content/20">
                            <table className="table table-sm w-full">
                                <thead className="bg-base-content/10 text-sm text-base-content/80">
                                    <tr>
                                        <th className="w-48 px-4 py-3">{t('KPI')}</th>
                                        <th className="px-4 py-3">{t('Example sensor name')}</th>
                                        <th className="px-4 py-3">{t('Description')}</th>
                                        <th className="px-4 py-3">{t('Unit')}</th>
                                        <th className="px-4 py-3">{t('Type')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-base-300">
                                    {sensorTypes.map((sensorType) => {
                                        const kpi = groupConfig.kpis[sensorType];
                                        const sensorName = deviceSensor.getSensorName(
                                            groupConfig.group,
                                            sensorType,
                                        );
                                        const sensorUrl = deviceSensor.getSensorUrl(
                                            groupConfig.group,
                                            sensorType,
                                        );

                                        return (
                                            <tr
                                                key={sensorType}
                                                className="even:bg-base-content/5 hover:bg-base-content/10"
                                            >
                                                <td className="align-top px-4 py-3">
                                                    <code className="whitespace-nowrap rounded bg-base-200 px-1.5 py-0.5 font-mono text-sm">
                                                        {sensorType}
                                                    </code>
                                                </td>
                                                <td className="align-top px-4 py-3">
                                                    {sensorName && sensorUrl ? (
                                                        <a
                                                            href={sensorUrl}
                                                            target="_blank"
                                                            rel="noreferrer noopener"
                                                            className="link link-hover break-all font-mono text-xs text-base-content/80 hover:text-base-content md:text-sm"
                                                        >
                                                            {sensorName}
                                                        </a>
                                                    ) : (
                                                        <code className="whitespace-nowrap rounded bg-base-200 px-1.5 py-0.5 font-mono text-sm">
                                                            N/A
                                                        </code>
                                                    )}
                                                </td>
                                                <td className="min-w-64 align-top px-4 py-3 whitespace-pre-wrap">
                                                    {t(kpi?.description ?? 'N/A')}
                                                </td>
                                                <td className="align-top px-4 py-3">
                                                    {kpi?.unit ?? 'N/A'}
                                                </td>
                                                <td className="align-top px-4 py-3">
                                                    {t(kpi?.active === false ? 'Outdated' : 'Current')}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </section>
                );
            })}
        </div>
    );
}

export default KpisTab;
